import './upload-arquivo-modal.js';
import './arquivo-detalhe-modal.js';
import type { Arquivo } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { PgKpiCard } from '@pgcustom/design-system';
import type { GaleriaApi } from './galeria.api.js';
import type { PermissoesGaleria } from './galeria.permissoes.js';
import type { ErpUploadArquivoModal } from './upload-arquivo-modal.js';
import type { ErpArquivoDetalheModal } from './arquivo-detalhe-modal.js';

const MIME_IMAGEM = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * `<erp-galeria-page>` — Galeria de Mídia (Sprint 12, `ROADMAP.md`:
 * "UI completa de gerenciamento de arquivos sobre o que o Storage já
 * entrega desde a Sprint 1"). Busca/filtro/tags/módulo já existiam no
 * backend (`GET /storage`) — esta é uma sprint inteiramente de
 * frontend, sem endpoint novo. Fonte de dado sempre real
 * (`GaleriaApi.listar`), nunca um array fixo — upload/exclusão afetam
 * o Storage de verdade.
 */
export class ErpGaleriaPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _galeriaApi: GaleriaApi | null = null;
  private _permissoes: PermissoesGaleria = { ler: false, criar: false, atualizar: false, excluir: false };

  private resultadoArquivos: Resultado<Arquivo[]> = { status: 'carregando' };
  private arquivosAtuais: Arquivo[] = [];
  private busca = '';
  private moduloFiltro = '';
  private pastaFiltro = '';

  /**
   * Opções dos seletores de módulo/pasta — atualizadas SÓ quando a
   * busca corrente não tem nenhum filtro ativo. Sem isso, escolher um
   * módulo estreitaria `arquivosAtuais` e faria o próprio seletor de
   * módulo "esquecer" as outras opções que existiam antes do filtro.
   */
  private opcoesModulo: string[] = [];
  private opcoesPasta: string[] = [];

  private modalUpload!: ErpUploadArquivoModal;
  private modalDetalhe!: ErpArquivoDetalheModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set galeriaApi(valor: GaleriaApi) {
    this._galeriaApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesGaleria) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._galeriaApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-5, 24px); flex-wrap: wrap; }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        .filtros { display: flex; gap: var(--pg-space-3, 12px); flex-wrap: wrap; margin-bottom: var(--pg-space-4, 16px); }
        ${FORM_FIELD_CSS}
        .filtros input, .filtros select { min-width: 160px; }
        .grade { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: var(--pg-space-4, 16px); }
        .cartao {
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          overflow: hidden;
          cursor: pointer;
          display: flex;
          flex-direction: column;
        }
        .cartao:hover { border-color: var(--pg-color-destaque, #2f8cff); }
        .cartao .miniatura { height: 120px; background: var(--pg-color-bg-base, #121417); display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .cartao .miniatura img { width: 100%; height: 100%; object-fit: cover; }
        .cartao .miniatura .extensao { color: var(--pg-color-texto-desabilitado, #5c626b); font-size: var(--pg-font-size-xs, 0.75rem); text-transform: uppercase; }
        .cartao .info { padding: var(--pg-space-3, 12px); display: flex; flex-direction: column; gap: var(--pg-space-2, 8px); }
        .cartao .nome { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-primario, #f2f3f5); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .cartao .meta { font-size: 0.6875rem; color: var(--pg-color-texto-desabilitado, #5c626b); }
        .cartao .badge-publico { position: absolute; top: var(--pg-space-2, 8px); right: var(--pg-space-2, 8px); }
        .cartao .miniatura { position: relative; }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); max-width: 560px; }
      </style>
      <div class="topo">
        <div>
          <h1>Galeria de Mídia</h1>
          <div class="subtitulo">Arquivos reais do Storage — busque, filtre por módulo/pasta e envie novos</div>
        </div>
        ${this._permissoes.criar ? '<pg-button id="botao-upload" variant="primary">Enviar arquivo</pg-button>' : ''}
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-total" label="Arquivos" icone="galeria" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-publicos" label="Públicos" icone="galeria" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-modulos" label="Módulos" icone="galeria" estado="carregando"></pg-kpi-card>
      </div>

      <div class="filtros">
        <input id="busca" type="text" placeholder="Buscar por nome…" value="${escaparAtributo(this.busca)}" />
        <select id="filtro-modulo">
          <option value="">Todos os módulos</option>
          ${this.opcoesModulo.map((m) => `<option value="${m}" ${m === this.moduloFiltro ? 'selected' : ''}>${m}</option>`).join('')}
        </select>
        <select id="filtro-pasta">
          <option value="">Todas as pastas</option>
          ${this.opcoesPasta.map((p) => `<option value="${p}" ${p === this.pastaFiltro ? 'selected' : ''}>${p}</option>`).join('')}
        </select>
      </div>
      <div id="arquivos"></div>

      <erp-upload-arquivo-modal id="modal-upload"></erp-upload-arquivo-modal>
      <erp-arquivo-detalhe-modal id="modal-detalhe"></erp-arquivo-detalhe-modal>
    `;

    this.modalUpload = this.root.querySelector('#modal-upload') as ErpUploadArquivoModal;
    if (this._galeriaApi) this.modalUpload.galeriaApi = this._galeriaApi;
    this.modalUpload.aoSalvar = () => void this.carregar();

    this.modalDetalhe = this.root.querySelector('#modal-detalhe') as ErpArquivoDetalheModal;
    if (this._galeriaApi) this.modalDetalhe.galeriaApi = this._galeriaApi;
    this.modalDetalhe.permissoes = { atualizar: this._permissoes.atualizar, excluir: this._permissoes.excluir };
    this.modalDetalhe.aoSalvar = () => void this.carregar();
    this.modalDetalhe.aoExcluir = () => void this.carregar();

    this.root.querySelector('#botao-upload')?.addEventListener('pg-click', () => this.modalUpload.abrir());

    const buscaInput = this.root.querySelector<HTMLInputElement>('#busca');
    let debounce: ReturnType<typeof setTimeout> | undefined;
    buscaInput?.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        this.busca = buscaInput.value;
        void this.carregar();
      }, 300);
    });

    this.root.querySelector('#filtro-modulo')?.addEventListener('change', (evento) => {
      this.moduloFiltro = (evento.target as HTMLSelectElement).value;
      void this.carregar();
    });
    this.root.querySelector('#filtro-pasta')?.addEventListener('change', (evento) => {
      this.pastaFiltro = (evento.target as HTMLSelectElement).value;
      void this.carregar();
    });

    this.atualizar();
  }

  private async carregar(): Promise<void> {
    const api = this._galeriaApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultadoArquivos = { status: 'sem-permissao' };
      this.atualizar();
      return;
    }

    this.resultadoArquivos = { status: 'carregando' };
    this.atualizar();
    const semNenhumFiltro = !this.busca && !this.moduloFiltro && !this.pastaFiltro;

    this.resultadoArquivos = await api.listar({
      ...(this.busca ? { query: this.busca } : {}),
      ...(this.moduloFiltro ? { module: this.moduloFiltro } : {}),
      ...(this.pastaFiltro ? { folder: this.pastaFiltro } : {}),
    });
    this.arquivosAtuais = this.resultadoArquivos.status === 'ok' ? this.resultadoArquivos.dado : [];

    if (semNenhumFiltro) {
      this.opcoesModulo = [...new Set(this.arquivosAtuais.map((a) => a.module))].sort();
      this.opcoesPasta = [...new Set(this.arquivosAtuais.map((a) => a.folder).filter((f): f is string => !!f))].sort();
    }

    this.renderEsqueleto();
  }

  private atualizar(): void {
    const container = this.root.querySelector('#arquivos');
    if (!container) return;

    if (this.resultadoArquivos.status === 'ok') {
      this.arquivosAtuais = this.resultadoArquivos.dado;
      container.innerHTML = `<div class="grade">${this.arquivosAtuais.map((a) => this.renderCartao(a)).join('')}</div>`;
      container.querySelectorAll<HTMLElement>('[data-arquivo-id]').forEach((el) => {
        el.addEventListener('click', () => {
          const arquivo = this.arquivosAtuais.find((a) => a.id === el.getAttribute('data-arquivo-id'));
          if (arquivo) this.modalDetalhe.abrir(arquivo);
        });
      });
      this.atualizarKpis();
      return;
    }

    this.arquivosAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoArquivos);
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-total', 'kpi-publicos', 'kpi-modulos']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultadoArquivos.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
  }

  /** KPIs sobre o RESULTADO FILTRADO atual (não o total absoluto do Storage) — honesto com o que a grade está mostrando neste momento. */
  private atualizarKpis(): void {
    const publicos = this.arquivosAtuais.filter((a) => a.isPublic).length;
    const modulos = new Set(this.arquivosAtuais.map((a) => a.module)).size;
    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('valor', String(this.arquivosAtuais.length));
    (this.root.querySelector('#kpi-publicos') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-publicos') as PgKpiCard | null)?.setAttribute('valor', String(publicos));
    (this.root.querySelector('#kpi-modulos') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-modulos') as PgKpiCard | null)?.setAttribute('valor', String(modulos));
  }

  private renderCartao(arquivo: Arquivo): string {
    const ehImagem = MIME_IMAGEM.includes(arquivo.mimeType);
    const urlDownload = this._galeriaApi?.urlDownload(arquivo.id) ?? '';
    return `
      <div class="cartao" data-arquivo-id="${arquivo.id}">
        <div class="miniatura">
          ${ehImagem ? `<img src="${urlDownload}" alt="${escaparAtributo(arquivo.originalName)}" loading="lazy" />` : `<span class="extensao">.${escaparHtml(arquivo.extension)}</span>`}
          ${arquivo.isPublic ? `<pg-badge tone="destaque" class="badge-publico">Público</pg-badge>` : ''}
        </div>
        <div class="info">
          <span class="nome" title="${escaparAtributo(arquivo.originalName)}">${escaparHtml(arquivo.originalName)}</span>
          <span class="meta">${escaparHtml(arquivo.module)}${arquivo.tags.length > 0 ? ' · ' + escaparHtml(arquivo.tags.join(', ')) : ''}</span>
        </div>
      </div>`;
  }
}

function estadoVazio(resultado: Resultado<Arquivo[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'Galeria de Mídia', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'Galeria de Mídia', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'Galeria de Mídia', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum arquivo', descricao: 'nenhum arquivo enviado ainda, ou nenhum resultado para este filtro' };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

function escaparAtributo(valor: string): string {
  return escaparHtml(valor).replace(/"/g, '&quot;');
}

customElements.define('erp-galeria-page', ErpGaleriaPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-galeria-page': ErpGaleriaPage;
  }
}
